import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    Action,
    type InternalUserProfileRequest as AuthInternalUserProfileRequest,
    ErrorType,
    type GUID,
    InternalUserProfile,
    type Tenant,
} from '@ndvr/auth-api';
import type { Tenant as CustomerTenant } from '@ndvr/customer-api';

import { INTERNAL_USERS_QUERY_KEY } from '@/modules/internal-user-queries';
import { AuthApi, CustomerApi } from 'API';
import { Toast } from 'widgets';

export type InternalUserProfileRecord = InternalUserProfile;
export type InternalUserProfileRequest = AuthInternalUserProfileRequest;

export type InternalUserTenantPermission = {
    tenant: Tenant;
    action: Action;
};

export const InternalUserProfileQueryKeys = {
    list: INTERNAL_USERS_QUERY_KEY,
    allTenants: ['internalUserProfile', 'allTenants'] as const,
    tenantPermissions: (internalUserGUID?: GUID<InternalUserProfile>) =>
        ['internalUserProfile', internalUserGUID, 'tenantPermissions'] as const,
};

export const useAllTenantsQuery = () =>
    useQuery<CustomerTenant[]>({
        queryKey: InternalUserProfileQueryKeys.allTenants,
        queryFn: async () => {
            const [tenants] = await CustomerApi().getTenants();
            return tenants ?? [];
        },
    });

export const useCreateInternalUserProfileMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (profile: InternalUserProfileRequest) => {
            await AuthApi().createInternalUserProfile(profile);
        },
        onSuccess: () => {
            Toast.success('Internal user created.');
            queryClient.invalidateQueries({
                queryKey: InternalUserProfileQueryKeys.list,
            });
        },
        onError: (e: unknown) => {
            const error = e as { errorType?: ErrorType };
            if (error.errorType === ErrorType.EX_CONFLICT) {
                Toast.info('An internal user with this email already exists.');
            } else {
                Toast.forError(e, 'Failed to create internal user.');
            }
        },
    });
};

export const useCreateTenantMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ name }: { name: string }) => {
            await CustomerApi().createTenant({ name });
        },
        onSuccess: () => {
            Toast.success('Tenant created.');
            queryClient.invalidateQueries({
                queryKey: InternalUserProfileQueryKeys.allTenants,
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to create tenant.');
        },
    });
};

export const useUpdateInternalUserProfileMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ profileGUID, profile }: { profileGUID: string; profile: InternalUserProfileRequest }) => {
            await AuthApi().updateInternalUserProfile(profileGUID, profile);
        },
        onSuccess: () => {
            Toast.success('Internal user updated.');
            queryClient.invalidateQueries({
                queryKey: InternalUserProfileQueryKeys.list,
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to update internal user.');
        },
    });
};

export const useDeleteInternalUserProfileMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (profileGUID: string) => {
            await AuthApi().archiveInternalUserProfile(profileGUID);
        },
        onSuccess: () => {
            Toast.success('Internal user deleted.');
            queryClient.invalidateQueries({
                queryKey: InternalUserProfileQueryKeys.list,
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to delete internal user.');
        },
    });
};

export const useInternalUserTenantPermissionsQuery = (internalUserGUID?: GUID<InternalUserProfile>) =>
    useQuery<InternalUserTenantPermission[]>({
        queryKey: InternalUserProfileQueryKeys.tenantPermissions(internalUserGUID),
        enabled: !!internalUserGUID,
        queryFn: async () => {
            if (!internalUserGUID) {
                return [];
            }

            const [tenants] = await AuthApi().getTenantsForInternalUser(internalUserGUID);
            const tenantList = tenants ?? [];
            const permissions = await Promise.all(
                tenantList.map(async (tenant) => {
                    const [action] = await AuthApi().getPermission(tenant.tenantGUID, internalUserGUID);
                    return {
                        tenant,
                        action: action ?? Action.NONE,
                    };
                }),
            );

            return permissions;
        },
    });

export const useGrantInternalUserTenantAccessMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            tenantGUID,
            internalUserGUID,
            action,
        }: {
            tenantGUID: GUID<Tenant>;
            internalUserGUID: GUID<InternalUserProfile>;
            action: Action;
        }) => {
            await AuthApi().grantInternalUserAccessToTenant(tenantGUID, internalUserGUID, { action });
        },
        onSuccess: (_response, variables) => {
            Toast.success('Tenant permission updated.');
            queryClient.invalidateQueries({
                queryKey: InternalUserProfileQueryKeys.tenantPermissions(variables.internalUserGUID),
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to update tenant permission.');
        },
    });
};

export const useRevokeInternalUserTenantAccessMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            tenantGUID,
            internalUserGUID,
        }: {
            tenantGUID: GUID<Tenant>;
            internalUserGUID: GUID<InternalUserProfile>;
        }) => {
            await AuthApi().revokeInternalUserAccessToTenant(tenantGUID, internalUserGUID);
        },
        onSuccess: (_response, variables) => {
            Toast.success('Tenant permission removed.');
            queryClient.invalidateQueries({
                queryKey: InternalUserProfileQueryKeys.tenantPermissions(variables.internalUserGUID),
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to remove tenant permission.');
        },
    });
};
